'use client';

import { ENGLISH_IQ_EVENTS } from '@repo/analytics';
import { useSession } from '@repo/auth';
import type { EnglishIqResponseV2Type, ICompleteEnglishIqV2Request } from '@repo/sdk';
import { getOrCreateVisitorId, trackEvent } from '@repo/web-chrome/lib/analytics';
import { type Dispatch, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  answerEnglishIqV2WebSession,
  completeEnglishIqV2WebSession,
  startEnglishIqV2WebSession,
} from '../actions';
import {
  clearEnglishIqV2StoredFlow,
  createEnglishIqV2WebState,
  type EnglishIqV2ClientItemType,
  type EnglishIqV2WebActionType,
  type IEnglishIqV2PendingAnswer,
  type IEnglishIqV2WebState,
  persistEnglishIqV2StoredFlow,
  readEnglishIqV2StoredFlow,
  reduceEnglishIqV2WebState,
  resolveEnglishIqV2RestoredFlow,
} from '../state';

interface IUseEnglishIqV2FlowParams {
  locale: string;
  sourceShareId: string | null;
}

export function useEnglishIqV2Flow({ locale, sourceShareId }: IUseEnglishIqV2FlowParams) {
  const ownerSubject = useEnglishIqV2OwnerSubject();
  const [state, dispatch] = useReducer(
    reduceEnglishIqV2WebState,
    sourceShareId,
    createEnglishIqV2WebState,
  );
  const identityMatches = ownerSubject !== null && state.ownerSubject === ownerSubject;
  useFlowStorage(state, sourceShareId, ownerSubject, dispatch);
  const start = useSessionStart(locale, sourceShareId, dispatch);
  const submitPendingCompletion = useSessionCompletion(sourceShareId, dispatch);
  const submitPendingAnswer = usePendingAnswerSubmission(state.assessment.currentItem, dispatch);
  const answer = useAnswerSubmission(state, submitPendingAnswer, dispatch);
  const retryPendingRequest = useRequestRetry(
    state,
    submitPendingAnswer,
    submitPendingCompletion,
    dispatch,
  );
  usePendingAnswerRecovery(state, submitPendingAnswer, identityMatches);
  usePendingCompletionRecovery(state, submitPendingCompletion, identityMatches);

  return {
    answer,
    finishDemo: () => dispatch({ type: 'demo_finished' }),
    markStartFailed: () => dispatch({ message: 'start_failed', type: 'request_failed' }),
    reset: () => {
      clearEnglishIqV2StoredFlow();
      dispatch({ type: 'reset' });
    },
    retryPendingRequest,
    start,
    state: identityMatches ? state : { ...state, hydrated: false },
  };
}

type EnglishIqV2DispatchType = Dispatch<EnglishIqV2WebActionType>;

function useFlowStorage(
  state: IEnglishIqV2WebState,
  sourceShareId: string | null,
  ownerSubject: string | null,
  dispatch: EnglishIqV2DispatchType,
): void {
  useEffect(() => {
    if (!ownerSubject) {
      return;
    }
    const storedFlow = readEnglishIqV2StoredFlow();
    const flow = resolveEnglishIqV2RestoredFlow(storedFlow, sourceShareId, ownerSubject);
    if (storedFlow && !flow) {
      clearEnglishIqV2StoredFlow();
    }
    dispatch({ flow, ownerSubject, type: 'hydrated' });
  }, [dispatch, ownerSubject, sourceShareId]);
  useEffect(() => {
    if (state.hydrated && state.ownerSubject && state.view !== 'start') {
      persistEnglishIqV2StoredFlow({ ...state, ownerSubject: state.ownerSubject });
    }
  }, [state]);
}

function useEnglishIqV2OwnerSubject(): string | null {
  const { data: session, isPending } = useSession();
  const [visitorSubject, setVisitorSubject] = useState<string | null>(null);
  const userId = session?.user?.id;
  useEffect(() => {
    if (!isPending && !userId && !visitorSubject) {
      setVisitorSubject(`visitor:${getOrCreateVisitorId()}`);
    }
  }, [isPending, userId, visitorSubject]);
  if (isPending) {
    return null;
  }
  return userId ? `user:${userId}` : visitorSubject;
}

function useSessionStart(
  locale: string,
  sourceShareId: string | null,
  dispatch: EnglishIqV2DispatchType,
) {
  const startingRef = useRef(false);
  return useCallback(
    async (turnstileToken?: string) => {
      if (startingRef.current) {
        return;
      }
      startingRef.current = true;
      try {
        const data = await startEnglishIqV2WebSession({
          locale,
          sourceShareId: sourceShareId ?? undefined,
          turnstileToken,
          visitorId: getOrCreateVisitorId(),
        });
        dispatch({ data, type: 'session_started' });
        trackEvent(ENGLISH_IQ_EVENTS.testStart, {
          source: sourceShareId ? 'friend_result' : 'landing',
          surface: 'web_v2',
        });
        if (sourceShareId) {
          trackEvent(ENGLISH_IQ_EVENTS.recipientStart, { surface: 'web_v2' });
        }
      } catch {
        dispatch({ message: 'start_failed', type: 'request_failed' });
      } finally {
        startingRef.current = false;
      }
    },
    [dispatch, locale, sourceShareId],
  );
}

function useSessionCompletion(sourceShareId: string | null, dispatch: EnglishIqV2DispatchType) {
  const completingRef = useRef<string | null>(null);
  return useCallback(
    async (request: ICompleteEnglishIqV2Request) => {
      if (completingRef.current === request.idempotencyId) {
        return;
      }
      completingRef.current = request.idempotencyId;
      try {
        const result = await completeEnglishIqV2WebSession(request);
        dispatch({ result, type: 'completion_received' });
        trackEvent(ENGLISH_IQ_EVENTS.testComplete, { score: result.score, surface: 'web_v2' });
        if (sourceShareId) {
          trackEvent(ENGLISH_IQ_EVENTS.recipientComplete, { surface: 'web_v2' });
        }
      } catch {
        dispatch({ message: 'complete_failed', type: 'request_failed' });
      } finally {
        completingRef.current = null;
      }
    },
    [dispatch, sourceShareId],
  );
}

function usePendingAnswerSubmission(
  item: EnglishIqV2ClientItemType | null,
  dispatch: EnglishIqV2DispatchType,
) {
  const retryingAnswerRef = useRef<string | null>(null);
  return useCallback(
    async (pendingAnswer: IEnglishIqV2PendingAnswer) => {
      if (retryingAnswerRef.current === pendingAnswer.idempotencyId) {
        return;
      }
      retryingAnswerRef.current = pendingAnswer.idempotencyId;
      try {
        const data = await answerEnglishIqV2WebSession(pendingAnswer);
        dispatch({ data, type: 'answer_received' });
        trackAnswerOutcome(item, pendingAnswer, data.replaced);
        if (data.completionReady) {
          dispatch({ type: 'demo_started' });
          dispatch({ idempotencyId: crypto.randomUUID(), type: 'completion_submitted' });
        }
      } catch {
        dispatch({ message: 'answer_failed', type: 'request_failed' });
      } finally {
        retryingAnswerRef.current = null;
      }
    },
    [dispatch, item],
  );
}

function useAnswerSubmission(
  state: IEnglishIqV2WebState,
  submitPendingAnswer: (pendingAnswer: IEnglishIqV2PendingAnswer) => Promise<void>,
  dispatch: EnglishIqV2DispatchType,
) {
  return useCallback(
    async (response: EnglishIqResponseV2Type, replayCount: number, responseTimeMs: number) => {
      const item = state.assessment.currentItem;
      const token = state.assessment.currentToken;
      if (!item || !token || state.assessment.phase !== 'active') {
        return;
      }
      const pendingAnswer = state.pendingAnswer ?? {
        idempotencyId: crypto.randomUUID(),
        itemId: item.id,
        replayCount,
        response,
        responseTimeMs,
        token,
      };
      dispatch({ pendingAnswer, type: 'answer_submitted' });
      if (isFinalScoredAnswer(state, response)) {
        dispatch({ type: 'demo_started' });
      }
      await submitPendingAnswer(pendingAnswer);
    },
    [dispatch, state, submitPendingAnswer],
  );
}

function useRequestRetry(
  state: IEnglishIqV2WebState,
  submitPendingAnswer: (answer: IEnglishIqV2PendingAnswer) => Promise<void>,
  submitPendingCompletion: (request: ICompleteEnglishIqV2Request) => Promise<void>,
  dispatch: EnglishIqV2DispatchType,
) {
  return useCallback(async () => {
    if (state.pendingAnswer) {
      dispatch({ pendingAnswer: state.pendingAnswer, type: 'answer_submitted' });
      await submitPendingAnswer(state.pendingAnswer);
      return;
    }
    const request = resolvePendingCompletionRequest(state);
    if (request) {
      await submitPendingCompletion(request);
    }
  }, [dispatch, state, submitPendingAnswer, submitPendingCompletion]);
}

function usePendingAnswerRecovery(
  state: IEnglishIqV2WebState,
  submitPendingAnswer: (answer: IEnglishIqV2PendingAnswer) => Promise<void>,
  identityMatches: boolean,
): void {
  useEffect(() => {
    if (
      identityMatches &&
      state.hydrated &&
      state.pendingAnswer &&
      state.assessment.phase === 'submitting'
    ) {
      void submitPendingAnswer(state.pendingAnswer);
    }
  }, [
    identityMatches,
    state.assessment.phase,
    state.hydrated,
    state.pendingAnswer,
    submitPendingAnswer,
  ]);
}

function usePendingCompletionRecovery(
  state: IEnglishIqV2WebState,
  submitPendingCompletion: (request: ICompleteEnglishIqV2Request) => Promise<void>,
  identityMatches: boolean,
): void {
  const completionId = state.pendingCompletionId;
  const token = state.assessment.currentToken;
  const hydrated = state.hydrated;
  useEffect(() => {
    if (identityMatches && hydrated && completionId && token) {
      void submitPendingCompletion({ idempotencyId: completionId, token });
    }
  }, [completionId, hydrated, identityMatches, submitPendingCompletion, token]);
}

function resolvePendingCompletionRequest(
  state: IEnglishIqV2WebState,
): ICompleteEnglishIqV2Request | null {
  if (!state.pendingCompletionId || !state.assessment.currentToken) {
    return null;
  }
  return {
    idempotencyId: state.pendingCompletionId,
    token: state.assessment.currentToken,
  };
}

function isFinalScoredAnswer(
  state: IEnglishIqV2WebState,
  response: EnglishIqResponseV2Type,
): boolean {
  return (
    response.kind !== 'media_unavailable' &&
    state.assessment.answeredCount === state.assessment.totalCount - 1
  );
}

function trackAnswerOutcome(
  item: EnglishIqV2ClientItemType | null,
  pendingAnswer: IEnglishIqV2PendingAnswer,
  replaced: boolean,
): void {
  if (!replaced) {
    trackEvent(ENGLISH_IQ_EVENTS.itemAnswered, {
      itemType: item?.type ?? 'unknown',
      skill: item?.skill ?? 'unknown',
      surface: 'web_v2',
    });
  }
  if (pendingAnswer.response.kind === 'media_unavailable') {
    trackEvent(ENGLISH_IQ_EVENTS.mediaFailure, {
      reason: pendingAnswer.response.reason,
      surface: 'web_v2',
    });
  }
}
